// src/components/common/BankDropdown.js
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';

const BankDropdown = ({ value, onChange, placeholder = "Select Bank", theme = 'light', required = false, className = "", disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBank, setSelectedBank] = useState('');
    const dropdownRef = useRef(null);

    // Comprehensive list of Indian banks
    const banks = [
        // Public Sector Banks
        "State Bank of India",
        "Punjab National Bank",
        "Bank of Baroda",
        "Bank of India",
        "Canara Bank",
        "Union Bank of India",
        "Indian Overseas Bank",
        "UCO Bank",
        "Bank of Maharashtra",
        "Punjab & Sind Bank",
        "Indian Bank",
        "Central Bank of India",
        "Allahabad Bank", // Now merged with Indian Bank
        "Andhra Bank", // Now merged with Union Bank of India
        "Corporation Bank", // Now merged with Union Bank of India
        "Oriental Bank of Commerce", // Now merged with Punjab National Bank
        "United Bank of India", // Now merged with Punjab National Bank
        "Vijaya Bank", // Now merged with Bank of Baroda
        "Dena Bank", // Now merged with Bank of Baroda
        "IDBI Limited", // Now IDBI Bank (Private)

        // Private Sector Banks
        "HDFC Bank",
        "ICICI Bank",
        "Axis Bank",
        "Kotak Mahindra Bank",
        "IndusInd Bank",
        "Yes Bank",
        "IDFC First Bank",
        "Federal Bank",
        "South Indian Bank",
        "Karur Vysya Bank",
        "RBL Bank",
        "Bandhan Bank",
        "Jammu & Kashmir Bank",
        "Tamilnad Mercantile Bank",
        "DCB Bank",
        "Ujjivan Small Finance Bank",
        "AU Small Finance Bank",
        "Equitas Small Finance Bank",
        "Utkarsh Small Finance Bank",
        "Fincare Small Finance Bank",
        "ESAF Small Finance Bank",
        "Suryoday Small Finance Bank",
        "North East Small Finance Bank",
        "Capital Small Finance Bank",
        "Shivalik Small Finance Bank",

        // Foreign Banks in India
        "Citibank",
        "HSBC Bank",
        "Standard Chartered Bank",
        "Deutsche Bank",
        "Barclays Bank",
        "American Express Banking",
        "BNP Paribas",
        "Credit Agricole",
        "Bank of America",
        "Mizuho Bank",
        "Sumitomo Mitsui Banking Corporation",
        "MUFG Bank",
        "DBS Bank",
        "United Overseas Bank",
        "ABSA Group (Barclays Africa)",

        // Payment Banks
        "Paytm Payments Bank",
        "PhonePe Payments Bank",
        "Google Pay (GPay) - Payment Bank Services",
        "Amazon Pay - Payment Bank Services",
        "Airtel Payments Bank",
        "Jio Payments Bank",
        "FINO Payments Bank",

        // Cooperative Banks (Major ones)
        "Saraswat Bank",
        "Cosmos Bank",
        "NKGSB Bank",
        "Shamrao Vithal Co-operative Bank",
        "RBL Bank" // Originally Ratnakar Bank
    ];

    // Filter banks based on search term
    const filteredBanks = banks.filter(bank =>
        bank.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update selected bank when value prop changes
    useEffect(() => {
        if (value !== selectedBank) {
            setSelectedBank(value);
        }
    }, [value, selectedBank]);

    const handleSelectBank = (bank) => {
        setSelectedBank(bank);
        onChange(bank);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e) => {
        if (disabled) return;
        const value = e.target.value;
        setSelectedBank(value);
        onChange(value);
        if (!isOpen) {
            setIsOpen(true);
        }
    };

    const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
    } ${disabled ? 'disabled:bg-gray-50 disabled:cursor-not-allowed' : ''} ${className}`;

    const dropdownClass = `absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border rounded-lg shadow-lg ${
        theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
    }`;

    const optionClass = `px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
    }`;

    const labelClass = `block text-sm font-medium mb-2 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
    }`;

    return (
        <div className="relative" ref={dropdownRef}>
            <label className={labelClass}>
                Bank Name {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={selectedBank}
                    onChange={handleInputChange}
                    onFocus={() => !disabled && setIsOpen(true)}
                    placeholder={placeholder}
                    className={inputClass}
                    required={required}
                    disabled={disabled}
                />
                <button
                    type="button"
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 disabled:opacity-50"
                    disabled={disabled}
                >
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''} ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`} />
                </button>
            </div>

            {isOpen && (
                <div className={dropdownClass}>
                    {/* Search Input */}
                    <div className="sticky top-0 z-10 p-2 border-b dark:border-gray-700">
                        <div className="relative">
                            <Search className={`absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`} />
                            <input
                                type="text"
                                placeholder="Search banks..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`w-full pl-8 pr-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
                                }`}
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Bank List */}
                    {filteredBanks.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                            No banks found
                        </div>
                    ) : (
                        <div>
                            {filteredBanks.map((bank, index) => (
                                <div
                                    key={index}
                                    className={optionClass}
                                    onClick={() => handleSelectBank(bank)}
                                >
                                    {bank}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BankDropdown;